import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    CircularProgress,
    MenuItem,
    Chip,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    SwapHoriz as SwapIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import axiosInstance from '../../../../../../API/axios';

// Custom debounce hook
const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);

    const debouncedCallback = useCallback((...args) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            callback(...args);
        }, delay);
    }, [callback, delay]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return debouncedCallback;
};

const ReassignSupportRequestDialog = ({
    open,
    onClose,
    supportRequest,
    onSaved
}) => {
    const [formData, setFormData] = useState({
        newProviderEmail: '',
        newProviderUsername: '',
        newProviderRegion: '',
        newProviderJobTitle: '',
        reason: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [emailExists, setEmailExists] = useState(null);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [regions, setRegions] = useState([]);

    useEffect(() => {
        if (open) {
            setFormData({
                newProviderEmail: '',
                newProviderUsername: '',
                newProviderRegion: '',
                newProviderJobTitle: '',
                reason: ''
            });
            setEmailExists(null);
            setError(null);
            fetchRegions();
        }
    }, [open]);

    const fetchRegions = async () => {
        try {
            const response = await axiosInstance.get('/region/all');
            setRegions(response.data || []);
        } catch (err) {
            console.error('Error fetching regions:', err);
        }
    };

    const checkEmailExistsAsync = useCallback(async (email) => {
        if (!email || !email.includes('@')) {
            setEmailExists(null);
            return;
        }

        setCheckingEmail(true);
        try {
            const response = await axiosInstance.get(`/users/check-email?email=${encodeURIComponent(email)}`);
            setEmailExists(response.data.exists);
        } catch (err) {
            console.error('Error checking email:', err);
            setEmailExists(null);
        } finally {
            setCheckingEmail(false);
        }
    }, []);

    const checkEmailExists = useDebounce(checkEmailExistsAsync, 500);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'newProviderEmail') {
            checkEmailExists(value);
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.newProviderEmail) {
            setError('New provider email is required');
            return;
        }
        if (!formData.newProviderUsername) {
            setError('New provider username is required');
            return;
        }
        if (!formData.reason) {
            setError('Reason for reassignment is required');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await axiosInstance.post(`/support-requests/${supportRequest.id}/reassign`, formData);
            onSaved();
            onClose();
        } catch (err) {
            console.error('Error reassigning support request:', err);
            setError(err.response?.data?.message || 'Failed to reassign support request');
        } finally {
            setSaving(false);
        }
    };

    if (!supportRequest) return null;

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <SwapIcon color="warning" />
                    <Typography variant="h6">
                        Reassign Support Request
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
                    <Typography variant="body2">
                        You are reassigning this support request from <strong>{supportRequest.providerUsername}</strong> ({supportRequest.providerEmail}) 
                        to a new provider. The current provider will be notified of this change.
                    </Typography>
                </Alert>

                <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Current Request Details
                    </Typography>
                    <Typography variant="body2">
                        <strong>Query:</strong> {supportRequest.supportQuery}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Created:</strong> {new Date(supportRequest.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Status:</strong>{' '}
                        <Chip label={supportRequest.status} size="small" color="warning" />
                    </Typography>
                </Box>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    New Provider Details
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                        name="newProviderEmail"
                        label="New Provider Email"
                        value={formData.newProviderEmail}
                        onChange={handleChange}
                        fullWidth
                        required
                        type="email"
                        InputProps={{
                            endAdornment: checkingEmail ? (
                                <CircularProgress size={20} />
                            ) : emailExists !== null ? (
                                <Chip
                                    label={emailExists ? 'Existing User' : 'New User'}
                                    size="small"
                                    color={emailExists ? 'success' : 'warning'}
                                />
                            ) : null
                        }}
                        helperText={
                            emailExists === false
                                ? 'This email is not registered. A new Support Provider account will be created.'
                                : ''
                        }
                    />

                    <TextField
                        name="newProviderUsername"
                        label="New Provider Username"
                        value={formData.newProviderUsername}
                        onChange={handleChange}
                        fullWidth
                        required
                        helperText={emailExists === false ? 'This will be the display name for the new user' : ''}
                    />

                    <Box display="flex" gap={2}>
                        <FormControl fullWidth>
                            <InputLabel>Region</InputLabel>
                            <Select
                                name="newProviderRegion"
                                value={formData.newProviderRegion}
                                onChange={handleChange}
                                label="Region"
                            >
                                <MenuItem value="">
                                    <em>Select Region</em>
                                </MenuItem>
                                {regions.map(region => (
                                    <MenuItem key={region.id} value={region.name}>
                                        {region.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            name="newProviderJobTitle"
                            label="Job Title"
                            value={formData.newProviderJobTitle}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Box>

                    <TextField
                        name="reason"
                        label="Reason for Reassignment"
                        value={formData.reason}
                        onChange={handleChange}
                        fullWidth
                        required
                        multiline
                        rows={3}
                        placeholder="Please explain why this support request needs to be reassigned..."
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handleSubmit}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <SwapIcon />}
                >
                    {saving ? 'Reassigning...' : 'Reassign Request'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReassignSupportRequestDialog;
